import React, { useState, useEffect } from 'react';
import { Breadcrumb, message, TreeSelect } from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import {
  Form,
  Input,
  Button,
  Space,
  Skeleton,
  ConfigProvider,
  BackTop,
  Checkbox,
} from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { useHistory } from 'react-router-dom';
import plPL from 'antd/lib/locale/pl_PL';
import 'moment/locale/pl';
import moment from 'moment-timezone';
import { modulesList } from 'enums/modulesDefinition';
import { mapModules } from 'helpers/moduleMapper';
import {
  getUserById,
  updateUser,
} from 'modules/userModule/request/userRequest';

const UserEdit = (): JSX.Element => {
  const { userId } = useParams<{ userId: string }>();
  const [form] = Form.useForm();
  const [userForm, setUserForm] = useState<FieldData[]>([]);
  const [loading, toggleLoading] = useState<boolean>(false);
  const [changePassCheck, toggleChangePass] = useState<boolean>(false);
  const history = useHistory();
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [modulesSelected, onChange] = useState<any>('');

  useEffect(() => {
    toggleLoading(true);
    getUserById(userId)
      .then((response) => {
        if (response) {
          setUserForm([
            {
              name: ['userId'],
              value: response.userId,
            },
            {
              name: ['name'],
              value: response.name,
            },
            {
              name: ['username'],
              value: response.username,
            },
            {
              name: ['modules'],
              value: JSON.parse(response.modules),
            },
          ]);
          toggleLoading(false);
        } else {
          history.push('/users');
        }
      })
      .catch(() => {
        history.push('/users');
      });
  }, []);

  const checkPasswordValidity = (value: string) => {
    const isNonWhiteSpace = /^\S*$/;
    if (!isNonWhiteSpace.test(value)) {
      return Promise.reject(new Error('Hasło nie może zawierać spacji!'));
    }

    const isContainsUppercase = /^(?=.*[A-Z]).*$/;
    if (!isContainsUppercase.test(value)) {
      return Promise.reject(
        new Error('Hasło musi zawierać co najmniej jedną wielką literę!')
      );
    }

    const isContainsLowercase = /^(?=.*[a-z]).*$/;
    if (!isContainsLowercase.test(value)) {
      return Promise.reject(
        new Error('Hasło musi zawierać co najmniej jedną małą literę!')
      );
    }

    const isContainsNumber = /^(?=.*[0-9]).*$/;
    if (!isContainsNumber.test(value)) {
      return Promise.reject(
        new Error('Hasło musi zawierać co najmniej jedną cyfrę!')
      );
    }

    const isContainsSymbol =
      /^(?=.*[~`!@#$%^&*()--+={}\[\]|\\:;"'<>,.?/_₹]).*$/;
    if (!isContainsSymbol.test(value)) {
      return Promise.reject(
        new Error('Hasło musi zawierać co najmniej jeden symbol specjalny.')
      );
    }

    const isValidLength = /^.{12,35}$/;
    if (!isValidLength.test(value)) {
      return Promise.reject(
        new Error('Hasło powinno mieć więcej niż 12 znaków!')
      );
    }

    if (value.replace(/\s/g, '').length < 12) {
      return Promise.reject(
        new Error('Hasło powinno mieć więcej niż 12 znaków!')
      );
    }
    return null;
  };

  const onFinish = async (values: any) => {
    const userValues = {
      username: values.username,
      modules: JSON.stringify(values.modules),
      password: values?.newPassword,
      userId: values.userId,
      name: values.name,
    };

    toggleButtonLoading(true);
    values.dateAdd = moment.utc(values.dateAdd).format();
    toggleButtonLoading(true);

    await updateUser(userValues).then((response) => {
      if (response?.data?.code === 1) {
        message.error('Proszę podać nowe hasło');
      } else {
        message.success('Pozytywnie zapisano');
      }
      toggleButtonLoading(false);
    });
  };

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const toggleCheckBox = () => {
    toggleChangePass(!changePassCheck);
    form.resetFields(['newPassword', 'repeatPassword']);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Ustawienia Administratorów</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/users">Administratorzy</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/users/${userId}`}>Edycja</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <div className="site-layout-background">
          {loading && <Skeleton active />}
          {!loading && (
            <Form
              layout="vertical"
              fields={userForm}
              onFinish={onFinish}
              form={form}
              validateMessages={validateMessages}
            >
              <Form.Item name="userId" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="name"
                label="Nazwa użytkownika"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="username"
                label="Login"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="changePassCheckBox" label="Zmień hasło">
                <Checkbox
                  defaultChecked={changePassCheck}
                  checked={changePassCheck}
                  onChange={() => toggleCheckBox()}
                >
                  Zmień hasło
                </Checkbox>
              </Form.Item>
              {changePassCheck && (
                <>
                  <Form.Item
                    name="newPassword"
                    label="Nowe hasło"
                    rules={[
                      {
                        required: true,
                      },
                      {
                        validator: async (_, value) => {
                          if (value) {
                            if (checkPasswordValidity(value)) {
                              return checkPasswordValidity(value);
                            }
                          }
                        },
                      },
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>
                  <Form.Item
                    name="confirm"
                    label="Potwerdź hasło"
                    dependencies={['password']}
                    rules={[
                      {
                        required: true,
                        message: 'Proszę potwierdzić hasło!',
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value ||
                            form.getFieldsValue()?.newPassword === value
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error('Hasła się nie zgadzają!')
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>
                </>
              )}
              <Form.Item
                name="modules"
                label="Moduły"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <TreeSelect
                  showSearch
                  value={modulesSelected}
                  dropdownStyle={{
                    maxHeight: 400,
                  }}
                  placeholder="Please select"
                  allowClear
                  multiple
                  treeDefaultExpandAll
                  virtual={false}
                  onChange={(data) => onChange(data)}
                  treeCheckable
                >
                  {Object.values(modulesList).map((el) => mapModules(el))}
                </TreeSelect>
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={isSaveButtonLoading}
                  >
                    Zapisz
                  </Button>
                  <Link to="/users">
                    <Button>Powrót</Button>
                  </Link>
                </Space>
              </Form.Item>
            </Form>
          )}
          {loading && (
            <Link to="/users">
              <Button>Powrót</Button>
            </Link>
          )}
          <BackTop />
        </div>
      </ConfigProvider>
    </>
  );
};

export default UserEdit;
